import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { resolveBrand } from "@/config/brand";
import { supportPanelBrand } from "@/lib/support-panel-brand";

export function useSupportTicketsRealtime(
  onChange: () => void,
  organizationId?: string | null,
  channelKey?: string,
) {
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const channel = supabase
      .channel(`support-tickets-${channelKey ?? organizationId ?? "all"}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "support_tickets",
          ...(organizationId ? { filter: `organization_id=eq.${organizationId}` } : {}),
        },
        () => {
          clearTimeout(timer);
          timer = setTimeout(() => latest.current(), 400);
        },
      )
      .subscribe();

    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [organizationId, channelKey]);
}

export function useSupportThreadRealtime(ticketId: string | null, onMessage: (row: any) => void) {
  const latest = useRef(onMessage);
  latest.current = onMessage;

  useEffect(() => {
    if (!ticketId) return;
    const channel = supabase
      .channel(`support-thread-${ticketId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_ticket_messages",
          filter: `ticket_id=eq.${ticketId}`,
        },
        (payload) => latest.current(payload.new),
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [ticketId]);
}

export interface UnreadTicket {
  id: string;
  subject: string;
}

export function useSupportUnreadReplies() {
  const [tickets, setTickets] = useState<UnreadTicket[]>([]);
  const { activeWorkspaceId: organizationId } = useAuth();

  const refresh = useCallback(async () => {
    if (!organizationId) { setTickets([]); return; }
    const { data, error } = await supabase
      .from("support_tickets")
      .select("id,subject")
      .eq("organization_id", organizationId)
      .eq("has_unseen_reply", true)
      .order("last_support_reply_at", { ascending: false });
    if (!error) setTickets(data ?? []);
  }, [organizationId]);

  useEffect(() => { refresh(); }, [refresh]);
  useSupportTicketsRealtime(refresh, organizationId);

  return tickets;
}

export function useSupportAssignedUnread() {
  const [tickets, setTickets] = useState<UnreadTicket[]>([]);
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const panelBrand = supportPanelBrand(resolveBrand().id);

  const refresh = useCallback(async () => {
    if (!userId) { setTickets([]); return; }
    const { data, error } = await supabase
      .from("support_tickets")
      .select("id,subject")
      .eq("assigned_to_user_id", userId)
      .eq("has_unseen_assignment", true)
      .eq("brand", panelBrand)
      .order("assigned_at", { ascending: false });
    if (!error) setTickets(data ?? []);
  }, [userId, panelBrand]);

  useEffect(() => { refresh(); }, [refresh]);
  useSupportTicketsRealtime(refresh, null, `assigned-${userId ?? "none"}`);

  return tickets;
}

export function useSupportPendingCount() {
  const [count, setCount] = useState<number | null>(null);
  const panelBrand = supportPanelBrand(resolveBrand().id);

  const refresh = useCallback(async () => {
    const { count: n, error } = await supabase
      .from("support_tickets")
      .select("id", { count: "exact", head: true })
      .eq("status", "open")
      .eq("brand", panelBrand);
    if (!error) setCount(n ?? 0);
  }, [panelBrand]);

  useEffect(() => { refresh(); }, [refresh]);
  useSupportTicketsRealtime(refresh);

  return count;
}
